<template>
  <div class="mobile-area-picker" :style="themeVariables">
    <div class="area-control">
      <button
        ref="trigger"
        class="area-trigger"
        type="button"
        aria-haspopup="dialog"
        :aria-expanded="visible ? 'true' : 'false'"
        :disabled="disabled"
        @click="open"
        @blur="onTriggerBlur"
      >
        <span class="area-value" :class="{ 'is-placeholder': !displayName }">{{ displayName || placeholder }}</span>
        <i class="el-icon-arrow-down" aria-hidden="true"></i>
      </button>
      <button
        v-if="value.length && !disabled"
        class="area-clear"
        type="button"
        aria-label="清空地区"
        @click="clear"
        @blur="onTriggerBlur"
      ><i class="el-icon-circle-close" aria-hidden="true"></i></button>
    </div>

    <div v-if="visible" ref="overlay" class="mobile-area-overlay" :style="themeVariables" @click.self="close">
      <section ref="dialog" class="area-dialog" role="dialog" aria-modal="true" aria-label="选择地区" tabindex="-1">
        <header class="area-heading">
          <h3>选择地区</h3>
          <button ref="closeButton" type="button" class="area-close" aria-label="关闭地区选择" @click="close">
            <i class="el-icon-close" aria-hidden="true"></i>
          </button>
        </header>
        <div class="area-tabs" role="tablist" aria-label="地区层级">
          <button
            v-for="(label, level) in levels"
            :key="label"
            type="button"
            role="tab"
            :aria-selected="activeLevel === level ? 'true' : 'false'"
            :disabled="loading || (level > 0 && !draftNodes[level - 1])"
            :class="{ 'is-active': activeLevel === level }"
            @click="loadLevel(level)"
          >{{ draftNodes[level] ? draftNodes[level].name : label }}</button>
        </div>
        <div ref="list" class="area-list" :aria-busy="loading ? 'true' : 'false'">
          <p v-if="loading" class="area-message" role="status">正在加载{{ levels[activeLevel] }}…</p>
          <div v-else-if="error" class="area-message" role="alert">
            <p>{{ error }}</p>
            <button type="button" class="area-retry" @click="loadLevel(activeLevel, true)">重新加载</button>
          </div>
          <p v-else-if="!options.length" class="area-message" role="status">暂无可选{{ levels[activeLevel] }}，请返回上一级选择</p>
          <div v-else role="group" :aria-label="levels[activeLevel]">
            <button
              v-for="option in options"
              :key="option.id"
              type="button"
              class="area-option"
              :class="{ 'is-selected': Number(draftIds[activeLevel]) === Number(option.id) }"
              :aria-pressed="Number(draftIds[activeLevel]) === Number(option.id) ? 'true' : 'false'"
              @click="choose(option)"
            >
              <span>{{ option.name }}</span>
              <i v-if="Number(draftIds[activeLevel]) === Number(option.id)" class="el-icon-check" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <footer class="area-actions">
          <button type="button" class="area-cancel" @click="close">取消</button>
          <button type="button" class="area-confirm" :disabled="!canConfirm" @click="confirm">确认</button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import Emitter from 'element-ui/src/mixins/emitter'

export default {
  name: 'MobileAreaPicker',
  mixins: [Emitter],
  props: {
    value: { type: Array, default: () => [] },
    disabled: Boolean,
    placeholder: { type: String, default: '请选择地区' },
    themeVariables: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      visible: false,
      levels: ['省', '市', '区县'],
      activeLevel: 0,
      draftIds: [],
      draftNodes: [],
      savedNodes: [],
      resolveFailed: false,
      options: [],
      loading: false,
      error: ''
    }
  },
  computed: {
    displayName() {
      if (!this.value.length) return ''
      if (this.savedNodes.length === this.value.length) return this.savedNodes.map(node => node.name).join(' / ')
      return this.resolveFailed ? '地区信息加载失败，点击重试' : '正在加载地区…'
    },
    canConfirm() {
      return !this.disabled && !this.loading && !this.error && this.options.length > 0 && this.draftIds.length === 3 && this.draftNodes.length === 3
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler() {
        if (this.visible) this.close()
        this.resolveValue()
        this.$nextTick(() => {
          if (!this.disposed) this.dispatch('ElFormItem', 'el.form.change', [this.value])
        })
      }
    },
    disabled(value) {
      if (value && this.visible) this.close()
    }
  },
  created() {
    this.requestVersion = 0
    this.disposed = false
    this.scrollSnapshot = null
  },
  beforeDestroy() {
    this.disposed = true
    this.requestVersion++
    this.valueVersion++
    document.removeEventListener('keydown', this.onDialogKeydown, true)
    this.unlockScroll()
    const overlay = this.$refs.overlay
    if (overlay && overlay.parentNode === document.body) document.body.removeChild(overlay)
  },
  methods: {
    async getOptions(level, ids) {
      const action = ['getProvinceList', 'getCityList', 'getDistrict'][level]
      const parentId = level === 0 ? 156 : Number(ids[level - 1])
      const nodes = await this.$store.dispatch(action, parentId)
      if (!Array.isArray(nodes)) throw new Error('Invalid region response')
      return nodes
    },
    async resolveValue() {
      const version = this.valueVersion = (this.valueVersion || 0) + 1
      const ids = this.value.map(Number)
      this.savedNodes = []
      this.resolveFailed = false
      if (!ids.length) return
      try {
        const nodes = []
        for (let level = 0; level < Math.min(ids.length, 3); level++) {
          const options = await this.getOptions(level, ids)
          if (this.disposed || version !== this.valueVersion) return
          const node = options.find(option => Number(option.id) === ids[level])
          if (!node) throw new Error('Region not found')
          nodes.push(node)
        }
        this.savedNodes = nodes
      } catch (error) {
        if (!this.disposed && version === this.valueVersion) this.resolveFailed = true
      }
    },
    open() {
      if (this.disabled || this.visible) return
      this.visible = true
      this.draftIds = this.value.slice(0, 3).map(Number)
      this.draftNodes = []
      this.lockScroll()
      document.addEventListener('keydown', this.onDialogKeydown, true)
      this.$nextTick(() => {
        if (!this.visible || this.disposed) return
        document.body.appendChild(this.$refs.overlay)
        this.$refs.dialog.focus()
      })
      this.loadLevel(0, true)
    },
    async loadLevel(level, restore = false) {
      if (!this.visible || (level > 0 && !this.draftNodes[level - 1])) return
      const version = ++this.requestVersion
      this.activeLevel = level
      this.options = []
      this.error = ''
      this.loading = true
      try {
        const options = await this.getOptions(level, this.draftIds)
        if (this.disposed || !this.visible || version !== this.requestVersion) return
        this.options = options
        this.loading = false
        if (restore && this.draftIds[level] !== undefined) {
          const node = options.find(option => Number(option.id) === this.draftIds[level])
          if (node) {
            this.$set(this.draftNodes, level, node)
            if (level < 2) return this.loadLevel(level + 1, true)
            if (this.draftIds.join(',') === this.value.map(Number).join(',')) {
              this.valueVersion++
              this.savedNodes = this.draftNodes.slice()
              this.resolveFailed = false
            }
          } else {
            this.draftIds = this.draftIds.slice(0, level)
            this.draftNodes = this.draftNodes.slice(0, level)
          }
        }
        this.$nextTick(() => {
          if (!this.disposed && this.visible && version === this.requestVersion && this.$refs.list) this.$refs.list.scrollTop = 0
        })
      } catch (error) {
        if (!this.disposed && this.visible && version === this.requestVersion) this.error = '地区加载失败，请重试'
      } finally {
        if (!this.disposed && version === this.requestVersion) this.loading = false
      }
    },
    choose(node) {
      if (this.loading || !this.visible) return
      const level = this.activeLevel
      this.draftIds = this.draftIds.slice(0, level).concat(Number(node.id))
      this.draftNodes = this.draftNodes.slice(0, level).concat(node)
      if (level < 2) this.loadLevel(level + 1)
    },
    confirm() {
      if (!this.canConfirm) return
      const ids = this.draftIds.slice()
      const nodes = this.draftNodes.slice()
      this.$emit('input', ids)
      this.$emit('change', ids, nodes)
      this.close()
    },
    clear() {
      if (this.disabled) return
      this.$emit('input', [])
      this.$emit('change', [], [])
      this.$nextTick(() => {
        if (this.disposed || this.visible) return
        this.$refs.trigger.focus()
        this.reportBlur()
      })
    },
    close() {
      if (!this.visible) return
      this.visible = false
      this.requestVersion++
      this.loading = false
      document.removeEventListener('keydown', this.onDialogKeydown, true)
      this.unlockScroll()
      this.$nextTick(() => {
        if (this.disposed || this.visible) return
        this.$refs.trigger.focus()
        this.reportBlur()
      })
    },
    onTriggerBlur(event) {
      if (!this.visible && !this.$el.contains(event.relatedTarget)) this.reportBlur()
    },
    reportBlur() {
      this.$emit('blur', this.value)
      this.dispatch('ElFormItem', 'el.form.blur', [this.value])
    },
    onDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        this.close()
      } else if (event.key === 'Tab') {
        const dialog = this.$refs.dialog
        if (!dialog) return
        const buttons = Array.from(dialog.querySelectorAll('button:not(:disabled)'))
        const first = buttons[0]
        const last = buttons[buttons.length - 1]
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault()
          first.focus()
        }
      }
    },
    lockScroll() {
      const body = document.body
      const html = document.documentElement
      this.scrollSnapshot = {
        x: window.pageXOffset,
        y: window.pageYOffset,
        body: ['position', 'top', 'left', 'width', 'overflow'].map(key => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]),
        htmlOverflow: [html.style.getPropertyValue('overflow'), html.style.getPropertyPriority('overflow')]
      }
      body.style.position = 'fixed'
      body.style.top = `-${this.scrollSnapshot.y}px`
      body.style.left = `-${this.scrollSnapshot.x}px`
      body.style.width = '100%'
      body.style.overflow = 'hidden'
      html.style.overflow = 'hidden'
    },
    unlockScroll() {
      const snapshot = this.scrollSnapshot
      if (!snapshot) return
      snapshot.body.forEach(([key, value, priority]) => document.body.style.setProperty(key, value, priority))
      document.documentElement.style.setProperty('overflow', ...snapshot.htmlOverflow)
      window.scrollTo(snapshot.x, snapshot.y)
      this.scrollSnapshot = null
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-area-picker,
.mobile-area-overlay {
  box-sizing: border-box;
  color: #606266;
  font-size: 0.875rem;
  line-height: 1.5;
  button {
    box-sizing: border-box;
    font: inherit;
    cursor: pointer;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    &:focus { outline: 2px solid var(--mobile-theme); outline-offset: -2px; }
    &:focus:not(:focus-visible) { outline: none; }
    &:disabled { cursor: not-allowed; }
  }
}
.area-control { display: flex; min-width: 0; }
.area-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  color: inherit;
  text-align: left;
  background: #fff;
  border: 1px solid #d9dfe8;
  border-radius: 0.25rem;
  &:focus { border-color: var(--mobile-theme); }
  &:disabled { color: #c0c4cc; background: #f5f7fa; }
  i { flex: 0 0 auto; margin-left: 0.5rem; color: #909399; }
}
.area-value { min-width: 0; overflow-wrap: break-word; word-break: break-word; white-space: normal; }
.is-placeholder { color: #a8abb2; }
.area-clear {
  flex: 0 0 2.75rem;
  padding: 0;
  color: #909399;
  background: transparent;
  border: 0;
}
.mobile-area-overlay {
  position: fixed;
  z-index: 3000;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}
.area-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 32rem;
  max-height: 80vh;
  max-height: 80dvh;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 0.75rem 0.75rem 0 0;
  outline: none;
}
.area-heading {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  h3 { margin: 0; color: #333; font-size: 1rem; font-weight: 500; }
}
.area-close {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  color: #666;
  background: transparent;
  border: 0;
}
.area-tabs {
  display: flex;
  flex: 0 0 auto;
  padding: 0 1rem;
  border-bottom: 1px solid #ebeef5;
  button {
    flex: 1;
    min-width: 0;
    min-height: 2.75rem;
    padding: 0.5rem 0.25rem;
    overflow-wrap: break-word;
    word-break: break-word;
    color: #666;
    background: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    &.is-active { color: var(--mobile-theme); border-bottom-color: var(--mobile-theme); }
    &:disabled { color: #a8abb2; }
  }
}
.area-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}
.area-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.625rem 1.25rem;
  color: inherit;
  text-align: left;
  background: #fff;
  border: 0;
  span { min-width: 0; overflow-wrap: break-word; word-break: break-word; }
  i { flex: 0 0 auto; margin-left: 0.75rem; }
  &.is-selected { color: var(--mobile-theme); background: var(--mobile-theme-light); }
}
.area-message { margin: 0; padding: 2rem 1rem; text-align: center; }
.area-retry { padding: 0.5rem 1rem; color: var(--mobile-theme); background: #fff; border: 1px solid var(--mobile-theme); border-radius: 0.25rem; }
.area-actions {
  display: flex;
  flex: 0 0 auto;
  padding: 0.75rem 1rem;
  padding-bottom: calc(0.75rem + constant(safe-area-inset-bottom));
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  border-top: 1px solid #ebeef5;
  button { flex: 1; min-width: 0; min-height: 2.75rem; padding: 0.5rem; border-radius: 0.25rem; }
}
.area-cancel { margin-right: 0.75rem; color: var(--mobile-theme); background: #fff; border: 1px solid var(--mobile-theme); }
.area-confirm { color: #fff; background: var(--mobile-button-background); border: 1px solid transparent; &:disabled { opacity: 0.45; } }
@media only screen and (min-width: 48rem) and (max-width: 80rem) {
  .mobile-area-picker,
  .mobile-area-overlay { font-size: 0.75rem; }
  .mobile-area-picker .area-trigger { min-height: 2.25rem; padding: 0.5rem 0.875rem; font-size: 0.6875rem; }
  .area-clear { flex-basis: 1.75rem; }
  .mobile-area-overlay { align-items: center; }
  .area-dialog { width: 22rem; height: 18rem; max-width: calc(100% - 2rem); border-radius: 0.5rem; }
  .area-heading { padding: 0.25rem 0.75rem; h3 { font-size: 0.875rem; } }
  .area-close { width: 1.75rem; height: 1.75rem; }
  .area-tabs { padding: 0 0.75rem; button { min-height: 1.75rem; padding: 0.375rem 0.25rem; } }
  .area-option { min-height: 1.75rem; padding: 0.375rem 0.875rem; }
  .area-actions { padding: 0.5rem 0.75rem; button { min-height: 1.75rem; padding: 0.375rem; } }
}
</style>
